import { defineComponent as _defineComponent } from 'vue'
import { ref, reactive, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import Spreadsheet from 'x-data-spreadsheet';
import { SpreadsheetData, SpreadsheetColumn } from '@/modules/Logistics/interfaces/LogisticsInterfaces';
import { downloadCSV } from '@/helpers/PrintHelper';
import { debounce } from 'vue-debounce';
import Address from '@/models/Address';
import Client from '@/models/Client';
// Import x-data-spreadsheet styles
import 'x-data-spreadsheet/dist/xspreadsheet.css';
import ClientRepository from '@/repositories/ClientRepository';

interface SummaryRowConfig {
    columnIndex: number;
    value: number | string;
}

interface ScrollState {
    contentTop: number;
    contentLeft: number;
    sheetHScrollLeft: number;
    sheetVScrollTop: number;
}

interface SpreadsheetConfig {
    viewHeight: number;
    viewWidth: number;
    rowCount: number;
    colCount: number;
    showToolbar?: boolean;
    showContextmenu?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'SpreadsheetEditor',
  props: {
    data: { default: null },
    columns: { default: () => [] },
    showToolbar: { type: Boolean, default: true },
    isLoading: { type: Boolean },
    loadingText: null,
    height: { default: 500 },
    readOnly: { type: Boolean, default: false },
    summaryRow: { default: null }
  },
  emits: ["data-change", "row-reorder", "column-reorder", "dropdown-select", "open-project"],
  setup(__props: any, { expose, emit }: { emit: ({
    (e: 'data-change', data: SpreadsheetData | null): void;
    (e: 'row-reorder', payload: { fromIndex: number; toIndex: number }): void;
    (e: 'column-reorder', payload: { fromIndex: number; toIndex: number }): void;
    (e: 'dropdown-select', payload: { value: string; row: number; col: number; columnKey: string }): void;
    (e: 'open-project', payload: { value: string; row: number; col: number; columnKey: string }): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { data: SpreadsheetData | null, columns: SpreadsheetColumn[], showToolbar: boolean, isLoading?: boolean, loadingText?: string, height: number, readOnly: boolean, summaryRow: SummaryRowConfig | null };





const containerRef = ref<HTMLElement | null>(null);
const spreadsheetRef = ref<HTMLElement | null>(null);
let spreadsheetInstance: Spreadsheet | null = null;

// Track selected row/column for context menu actions
const selectedRowIndex = ref<number | null>(null);
const selectedColIndex = ref<number | null>(null);

const rowCount = ref(0);
const columnCount = ref(0);

// Consolidated dropdown UI state (single source of truth)
const dropdown = reactive({
    visible: false,
    justOpened: false,
    position: { top: 0, left: 0, width: 100 },
    cell: null as { row: number; col: number; columnKey: string } | null,
    currentValue: '',
    searchQuery: '',
    isLoading: false,
    options: [] as string[],
    hasPreMatchedOptions: false,
});

const dropdownSearchInputRef = ref<HTMLInputElement | null>(null);

// Per-row persistent state: tracks client ID, cached addresses, and address validity
const rowState = reactive<
    Record<
        number,
        {
            clientId?: string;
            clientAddresses?: string[];
            addressValid?: boolean;
        }
    >
>({});

// Temporary client name → ID map populated during client search
let clientNameToIdMap: Record<string, string> = {};

// Scroll position preservation helpers
function saveScrollPosition(): ScrollState {
    // Save delivery-transformator__content scroll (parent container)
    const contentEl = document.querySelector('.delivery-transformator__content');
    const contentTop = contentEl?.scrollTop || 0;
    const contentLeft = contentEl?.scrollLeft || 0;

    // Save spreadsheet custom scrollbar positions (x-data-spreadsheet uses custom scrollbars)
    const hScrollbar = spreadsheetRef.value?.querySelector('.x-spreadsheet-scrollbar.horizontal');
    const vScrollbar = spreadsheetRef.value?.querySelector('.x-spreadsheet-scrollbar.vertical');
    const sheetHScrollLeft = hScrollbar?.scrollLeft || 0;
    const sheetVScrollTop = vScrollbar?.scrollTop || 0;

    return { contentTop, contentLeft, sheetHScrollLeft, sheetVScrollTop };
}

function restoreScrollPosition(pos: ScrollState) {
    // Restore delivery-transformator__content scroll
    const contentEl = document.querySelector('.delivery-transformator__content');
    if (contentEl) {
        contentEl.scrollTop = pos.contentTop;
        contentEl.scrollLeft = pos.contentLeft;
    }

    // Restore spreadsheet custom scrollbar positions
    const hScrollbar = spreadsheetRef.value?.querySelector('.x-spreadsheet-scrollbar.horizontal');
    const vScrollbar = spreadsheetRef.value?.querySelector('.x-spreadsheet-scrollbar.vertical');

    if (hScrollbar && pos.sheetHScrollLeft > 0) {
        hScrollbar.scrollLeft = pos.sheetHScrollLeft;
        // Trigger scroll event so the canvas updates
        hScrollbar.dispatchEvent(new Event('scroll'));
    }

    if (vScrollbar && pos.sheetVScrollTop > 0) {
        vScrollbar.scrollTop = pos.sheetVScrollTop;
        vScrollbar.dispatchEvent(new Event('scroll'));
    }
}

// Filtered dropdown options - simple local filter on current options
const filteredDropdownOptions = computed(() => {
    const query = dropdown.searchQuery.toLowerCase().trim();
    const options = dropdown.options;
    return query ? options.filter((o) => o.toLowerCase().includes(query)) : options;
});

const showTypeMoreHint = computed(() => {
    return (
        filteredDropdownOptions.value.length === 0 &&
        !dropdown.isLoading &&
        dropdown.searchQuery.length < 2 &&
        !dropdown.hasPreMatchedOptions
    );
});

const dropdownStyle = computed(() => ({
    position: 'absolute' as const,
    top: `${dropdown.position.top}px`,
    left: `${dropdown.position.left}px`,
    width: `${dropdown.position.width}px`,
    zIndex: 9999,
}));

const baseConfig = computed<SpreadsheetConfig>(() => {
    let rowCount = Object.keys(props.data?.rows || {}).length + 1;
    let columnCount = Object.keys(props.data?.rows?.[0]?.cells || {}).length + 1;

    if (rowCount < 12) {
        rowCount = 12;
    }

    if (columnCount < 12) {
        columnCount = 12;
    }

    return {
        viewHeight: spreadsheetRef.value?.clientHeight || props.height,
        viewWidth: spreadsheetRef.value?.clientWidth || 800,
        rowCount,
        colCount: columnCount,
    };
});
function updateCounts(data: SpreadsheetData) {
    if (!data || !data.rows) {
        rowCount.value = 0;
        columnCount.value = 0;
        return;
    }

    const rowKeys = Object.keys(data.rows);
    rowCount.value = Math.max(0, rowKeys.length - 1); // Subtract header row

    // Get column count from first row
    const firstRow = data.rows[0];
    if (firstRow && firstRow.cells) {
        columnCount.value = Object.keys(firstRow.cells).length;
    } else {
        columnCount.value = 0;
    }
}

/**
 * Extends the spreadsheet context menu with custom row/column reorder options
 */
function extendContextMenu(instance: Spreadsheet) {
    if (!instance || !spreadsheetRef.value) {
        return;
    }

    // Listen for cell selection to track current row/column and show dropdown
    instance.on('cell-selected', (cell: any, ri: number, ci: number) => {
        selectedRowIndex.value = ri;
        selectedColIndex.value = ci;

        // Hide dropdown first
        hideDropdown();

        // Check if this column has dropdown options (skip header row)
        if (ri > 0 && props.columns && props.columns[ci]) {
            const column = props.columns[ci];
            if (column.type === 'dropdown') {
                // Check for row-specific dropdown options first, then fall back to column-level
                const rowSpecificOptions = column.rowDropdownOptions?.[ri];
                const options = rowSpecificOptions || column.dropdownOptions || [];

                // Show dropdown even with empty options - user can search via API
                showDropdownForCell(ri, ci, column, options);
            }
        }
    });

    // Add custom context menu by intercepting right-click
    spreadsheetRef.value.addEventListener('contextmenu', (e: MouseEvent) => {
        // Let the default context menu show first, then add our items
        setTimeout(() => {
            addCustomContextMenuItems();
        }, 10);
    });
}

/**
 * Shows the dropdown overlay for a specific cell
 * @param row - Spreadsheet row index (1-based, since row 0 is header)
 * @param col - Column index
 * @param column - Column configuration
 * @param options - Dropdown options to display (row-specific or column-level, can be empty for API search)
 */
async function showDropdownForCell(row: number, col: number, column: SpreadsheetColumn, preMatchedOptions: string[]) {
    if (!spreadsheetRef.value || !containerRef.value) return;

    const selectedCell = spreadsheetRef.value.querySelector('.x-spreadsheet-selector-area');
    if (!selectedCell) return;

    const cellRect = selectedCell.getBoundingClientRect();
    const containerRect = containerRef.value.getBoundingClientRect();
    const currentData = getData();
    if (!currentData?.rows?.[row]) return;

    const cellValue = currentData.rows[row]?.cells?.[col]?.text || '';

    Object.assign(dropdown, {
        visible: true,
        justOpened: true,
        position: {
            top: cellRect.top - containerRect.top + cellRect.height,
            left: cellRect.left - containerRect.left,
            width: Math.max(cellRect.width, 200),
        },
        cell: { row, col, columnKey: column.key },
        currentValue: cellValue,
        searchQuery: '',
        isLoading: false,
        options: preMatchedOptions,
        hasPreMatchedOptions: preMatchedOptions.length > 0,
    });

    nextTick(() => dropdownSearchInputRef.value?.focus());
    setTimeout(() => {
        dropdown.justOpened = false;
    }, 300);

    // Address dropdown: auto-fetch by client ID on open (if no pre-matched options)
    if (column.key === '_addressDropdown' && !preMatchedOptions.length) {
        const clientId = rowState[row]?.clientId;
        if (clientId) {
            await fetchAddressesByClient(clientId, row);
            dropdown.isLoading = false;
        }
    }
}

/**
 * Hides the dropdown overlay
 */
function hideDropdown() {
    if (dropdown.justOpened) return;
    dropdown.visible = false;
    dropdown.cell = null;
    dropdown.searchQuery = '';
    dropdown.isLoading = false;
    dropdown.options = [];
}

// ============================================================================
// DROPDOWN SEARCH
// ============================================================================

async function fetchAddressesByClient(clientId: string, row: number): Promise<boolean> {
    try {
        dropdown.isLoading = true;
        const response = await Address.searchByClientID(clientId);
        const addresses = (response?.data || []).map(
            (addr: any) => `${addr?.city || ''}, ${addr?.streetNameAndNumber || ''}`,
        );
        dropdown.options = addresses;
        if (!rowState[row]) rowState[row] = {};
        rowState[row].clientAddresses = addresses;
        return addresses.length > 0;
    } catch {
        dropdown.isLoading = false;
        return false;
    }
}

async function fetchAddressesGeneral(query: string): Promise<void> {
    try {
        const response = await Address.search(query);
        dropdown.options = (response?.data || []).map(
            (addr: any) => `${addr?.city || ''}, ${addr?.streetNameAndNumber || ''}`,
        );
    } catch {
        dropdown.options = [];
    } finally {
        dropdown.isLoading = false;
    }
}

async function fetchClients(query: string): Promise<void> {
    try {
        await Client.searchWithAddresses(query);
        const clients = ClientRepository.getAll();
        const row = dropdown.cell?.row;

        clientNameToIdMap = {};

        for (const client of clients || []) {
            if (!client.name || !client.id) {
                continue;
            }

            clientNameToIdMap[client.name] = client.id;

            if (row == null) {
                continue;
            }

            if (!client.addresses || !Array.isArray(client.addresses)) {
                continue;
            }

            if (!rowState[row]) {
                rowState[row] = {};
            }

            if (!rowState[row].clientAddresses) {
                rowState[row].clientAddresses = [];
            }

            for (const addr of client.addresses) {
                const str = `${addr?.city || ''}, ${addr?.streetNameAndNumber || ''}`;

                if (str.trim() === ',') {
                    continue;
                }

                if (rowState[row].clientAddresses.includes(str)) {
                    continue;
                }

                rowState[row].clientAddresses.push(str);
            }
        }

        dropdown.options = (clients || []).map((client: any) => client.name || '');
    } catch {
        dropdown.options = [];
    } finally {
        dropdown.isLoading = false;
    }
}

const debouncedSearch = debounce(async () => {
    const query = dropdown.searchQuery.trim();
    const columnKey = dropdown.cell?.columnKey;
    const row = dropdown.cell?.row;

    if (columnKey === '_addressDropdown' && row != null) {
        const clientId = rowState[row]?.clientId;
        if (clientId) {
            const found = await fetchAddressesByClient(clientId, row);
            if (!found) {
                await fetchAddressesGeneral(query);
                return;
            }

            dropdown.isLoading = false;
        } else {
            await fetchAddressesGeneral(query);
        }
    } else if (columnKey === '_clientDropdown') {
        await fetchClients(query);
    }
}, 2000);

function handleDropdownSearchInput() {
    if (dropdown.hasPreMatchedOptions) return;

    const query = dropdown.searchQuery.trim();
    if (query.length > 2) {
        dropdown.isLoading = true;
        debouncedSearch();
    } else {
        dropdown.options = [];
    }
}

/**
 * Handles clicking on a dropdown option
 */
function handleDropdownOptionClick(value: string) {
    if (!dropdown.cell) return;
    const { row, col, columnKey } = dropdown.cell;
    const currentData = getData();

    // Save scroll position BEFORE any data changes
    const scrollPos = saveScrollPosition();

    if (columnKey === '_clientDropdown') {
        handleClientSelection(value, row, currentData);
        // Only mark Stranka as valid — address cells still need validation
        const strankaIdx = props.columns.findIndex((c) => c.key === 'Stranka');
        markCellsValid(row, [strankaIdx]);
    }

    if (columnKey === '_addressDropdown') {
        if (!rowState[row]) rowState[row] = {};
        rowState[row].addressValid = true;
        // Mark Kraj and Naslov as valid
        const krajIdx = props.columns.findIndex((c) => c.key === 'Kraj');
        const naslovIdx = props.columns.findIndex((c) => c.key === 'Naslov');
        markCellsValid(row, [krajIdx, naslovIdx]);
    }

    if (currentData?.rows?.[row]) {
        emit('data-change', currentData);
        emit('dropdown-select', { value, row, col, columnKey });
    }

    hideDropdown();

    // Restore scroll position AFTER all operations (double RAF for safety)
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            restoreScrollPosition(scrollPos);
        });
    });
}

/**
 * Handles client selection: updates rowState client ID
 */
function handleClientSelection(clientName: string, row: number, currentData: SpreadsheetData | null) {
    if (!rowState[row]) rowState[row] = {};

    if (!clientName?.trim()) {
        delete rowState[row].clientId;
        delete rowState[row].clientAddresses;
    } else {
        // Resolve client ID: prefer pre-matched from validation, then API results
        const clientColumn = props.columns.find((c) => c.key === '_clientDropdown');
        const preMatchedId = clientColumn?.rowClientNameToIdMap?.[row]?.[clientName];
        rowState[row].clientId = preMatchedId || clientNameToIdMap[clientName] || undefined;

        if (!rowState[row].clientId) {
            delete rowState[row].clientAddresses;
        }
    }
}

/**
 * Adds custom menu items to the existing context menu
 */
function addCustomContextMenuItems() {
    const contextMenu = document.querySelector('.x-spreadsheet-contextmenu');
    if (!contextMenu) {
        return;
    }

    // Remove existing custom items first (so we can rebuild based on current selection)
    contextMenu.querySelectorAll('.custom-menu-item, .custom-menu-divider').forEach((item) => item.remove());

    // Add separator
    const separator = document.createElement('div');
    separator.className = 'x-spreadsheet-item divider custom-menu-divider';
    separator.style.borderTop = '1px solid #e0e0e0';
    separator.style.margin = '4px 0';
    contextMenu.appendChild(separator);

    // Add row reorder items
    const moveRowUp = createMenuItem('↑ Move Row Up', () => moveRow('up'));
    const moveRowDown = createMenuItem('↓ Move Row Down', () => moveRow('down'));

    // Add column reorder items
    const moveColLeft = createMenuItem('← Move Column Left', () => moveColumn('left'));
    const moveColRight = createMenuItem('→ Move Column Right', () => moveColumn('right'));

    contextMenu.appendChild(moveRowUp);
    contextMenu.appendChild(moveRowDown);
    contextMenu.appendChild(moveColLeft);
    contextMenu.appendChild(moveColRight);

    // Only append open project item for "St.Dok." column
    if (selectedColIndex.value !== null && props.columns[selectedColIndex.value]?.key === 'St.Dok.') {
        const openProjectItem = createMenuItem('🔍 Search order', () =>
            emit('open-project', {
                value: 'open_project',
                row: selectedRowIndex.value!,
                col: selectedColIndex.value!,
                columnKey: 'St.Dok.',
            }),
        );
        contextMenu.prepend(openProjectItem);
    }
}

/**
 * Creates a custom context menu item element
 */
function createMenuItem(label: string, onClick: () => void): HTMLElement {
    const item = document.createElement('div');
    item.className = 'x-spreadsheet-item custom-menu-item';
    item.textContent = label;
    item.style.padding = '8px 16px';

    item.addEventListener('mouseenter', () => {
        item.style.backgroundColor = '#f0f0f0';
    });
    item.addEventListener('mouseleave', () => {
        item.style.backgroundColor = '';
    });
    item.addEventListener('click', (e) => {
        e.stopPropagation();
        onClick();
        // Close the context menu
        const contextMenu = document.querySelector('.x-spreadsheet-contextmenu') as HTMLElement;
        if (contextMenu) {
            contextMenu.style.display = 'none';
        }
    });

    return item;
}

/**
 * Moves the selected row up or down
 */
function moveRow(direction: 'up' | 'down') {
    if (selectedRowIndex.value === null) {
        return;
    }

    const currentData = getData();
    if (!currentData || !currentData.rows) {
        return;
    }

    const fromIndex = selectedRowIndex.value;
    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;

    // Validate bounds (row 0 is header, don't move it)
    if (fromIndex <= 0 || toIndex <= 0 || toIndex >= Object.keys(currentData.rows).length) {
        return;
    }

    // Swap rows in data
    const newRows: typeof currentData.rows = {};
    const rowKeys = Object.keys(currentData.rows)
        .map(Number)
        .sort((a, b) => a - b);

    for (const key of rowKeys) {
        let targetKey = key;
        if (key === fromIndex) {
            targetKey = toIndex;
        } else if (key === toIndex) {
            targetKey = fromIndex;
        }
        newRows[targetKey] = currentData.rows[key];
    }

    currentData.rows = newRows;

    // Reload data and emit event
    loadData(currentData);
    selectedRowIndex.value = toIndex;
    emit('data-change', currentData);
}

/**
 * Moves the selected column left or right
 */
function moveColumn(direction: 'left' | 'right') {
    if (selectedColIndex.value === null) {
        return;
    }

    const currentData = getData();
    if (!currentData || !currentData.rows) {
        return;
    }

    const fromIndex = selectedColIndex.value;
    const toIndex = direction === 'left' ? fromIndex - 1 : fromIndex + 1;

    // Validate bounds
    if (fromIndex < 0 || toIndex < 0) {
        return;
    }

    // Get max column index
    const firstRow = currentData.rows[0];
    if (!firstRow || !firstRow.cells) {
        return;
    }
    const maxColIndex = Math.max(...Object.keys(firstRow.cells).map(Number));

    if (toIndex > maxColIndex) {
        return;
    }

    // Swap columns in each row
    for (const rowKey of Object.keys(currentData.rows)) {
        const row = currentData.rows[parseInt(rowKey)];

        if (!row || !row.cells) {
            continue;
        }

        const fromCell = row.cells[fromIndex];
        const toCell = row.cells[toIndex];

        row.cells[fromIndex] = toCell || { text: '' };
        row.cells[toIndex] = fromCell || { text: '' };
    }

    // Swap column widths if defined
    if (currentData.cols) {
        const fromWidth = currentData.cols[fromIndex];
        const toWidth = currentData.cols[toIndex];
        currentData.cols[fromIndex] = toWidth || { width: 100 };
        currentData.cols[toIndex] = fromWidth || { width: 100 };
    }

    // Reload data and emit event
    loadData(currentData);
    selectedColIndex.value = toIndex;
    emit('data-change', currentData);
}

function getData(): SpreadsheetData | null {
    if (!spreadsheetInstance) {
        return null;
    }

    return spreadsheetInstance.getData()[0] as SpreadsheetData;
}

/**
 * Marks specific cells as valid (NORMAL_STYLE) for a given row.
 * Used after user edits a cell to permanently clear red validation.
 */
function markCellsValid(row: number, colIndices: number[]) {
    if (!spreadsheetInstance) return;

    const NORMAL_STYLE = 1;
    const dataProxy = (spreadsheetInstance as any).datas?.[0];
    if (!dataProxy?.rows) return;

    for (const colIdx of colIndices) {
        if (colIdx >= 0) {
            dataProxy.rows.setCell(row, colIdx, { style: NORMAL_STYLE }, 'format');
        }
    }

    (spreadsheetInstance as any).reRender();
}

/**
 * Updates cell validation styles based on client/address selection state
 * Only used on initial mount / reload - not on subsequent user edits.
 * - Stranka (client) cell: RED if no client ID selected for that row
 * - Kraj and Naslov cells: RED if no address selected OR address not from client
 */
function updateCellValidationStyles() {
    const currentData = getData();
    if (!currentData || !currentData.rows) {
        return;
    }

    // Find column indices
    const strankaColIndex = props.columns.findIndex((col) => col.key === 'Stranka');
    const krajColIndex = props.columns.findIndex((col) => col.key === 'Kraj');
    const naslovColIndex = props.columns.findIndex((col) => col.key === 'Naslov');

    // Ensure we have an error style (style index 3 = red background)
    if (!currentData.styles) {
        currentData.styles = [];
    }
    // Add error style if not exists (assuming styles 0,1,2 already exist)
    if (currentData.styles.length < 4) {
        currentData.styles[3] = {
            bgcolor: '#ffcccc',
            font: { size: 10 },
            align: 'left',
        };
    }

    const ERROR_STYLE = 3;
    const NORMAL_STYLE = 1;

    // Get all data rows (skip header at row 0)
    const rowKeys = Object.keys(currentData.rows).filter((k) => parseInt(k) > 0);

    for (const rowKey of rowKeys) {
        const rowIndex = parseInt(rowKey);
        const row = currentData.rows[rowIndex];
        if (!row || !row.cells) {
            continue;
        }

        // Check if client is valid for this row (matched or fetched)
        const strankaCell = row.cells[strankaColIndex];
        const clientName = strankaCell?.text?.trim() || '';
        const hasValidClient = isValidClientName(clientName, rowIndex);

        // Check if address is valid (selected from client's addresses)
        const addressValid = rowState[rowIndex]?.addressValid === true;

        // Check if Kraj and Naslov cells are empty
        const krajCell = row.cells[krajColIndex];
        const naslovCell = row.cells[naslovColIndex];
        const krajEmpty = !krajCell?.text?.trim();
        const naslovEmpty = !naslovCell?.text?.trim();

        // Apply Stranka style
        if (strankaColIndex >= 0 && row.cells[strankaColIndex]) {
            row.cells[strankaColIndex].style = hasValidClient ? NORMAL_STYLE : ERROR_STYLE;
        }

        // Apply Kraj style: RED if empty OR address not from client
        if (krajColIndex >= 0 && row.cells[krajColIndex]) {
            const shouldBeRed = krajEmpty || (!addressValid && hasValidClient);
            row.cells[krajColIndex].style = shouldBeRed ? ERROR_STYLE : NORMAL_STYLE;
        }

        // Apply Naslov style: RED if empty OR address not from client
        if (naslovColIndex >= 0 && row.cells[naslovColIndex]) {
            const shouldBeRed = naslovEmpty || (!addressValid && hasValidClient);
            row.cells[naslovColIndex].style = shouldBeRed ? ERROR_STYLE : NORMAL_STYLE;
        }
    }

    // Reload data with updated styles
    loadData(currentData);
}

/**
 * Checks if a row has an initial client match from validation
 */
function hasInitialClientMatch(rowIndex: number): boolean {
    const addressColumn = props.columns.find((col) => col.key === '_addressDropdown');
    return !!addressColumn?.rowMatchedClientIds?.[rowIndex];
}

/**
 * Checks if a client name is valid for a given row
 * Valid means: matched from initial validation OR fetched from API
 */
function isValidClientName(clientName: string, rowIndex: number): boolean {
    if (!clientName || !clientName.trim()) {
        return false;
    }

    // Check 1: Is it a matched client from initial validation (rowClientNameToIdMap)?
    const clientColumn = props.columns.find((c) => c.key === '_clientDropdown');
    if (clientColumn?.rowClientNameToIdMap?.[rowIndex]?.[clientName]) {
        return true;
    }

    // Check 2: Is it a fetched client from API search?
    if (clientNameToIdMap[clientName]) {
        return true;
    }

    // Check 3: Do we have a tracked client ID for this row?
    if (rowState[rowIndex]?.clientId) {
        return true;
    }

    return false;
}

function handleExport() {
    const data = getData();
    if (!data || !data.rows) {
        return;
    }

    // Build CSV content row by row
    const csvLines: string[] = [];
    const rowKeys = Object.keys(data.rows);

    // Sort row keys numerically
    rowKeys.sort((a, b) => parseInt(a) - parseInt(b));

    for (const rowKey of rowKeys) {
        const row = data.rows[parseInt(rowKey)];
        if (!row || !row.cells) {
            continue;
        }

        const cells = row.cells;
        const cellKeys = Object.keys(cells);

        // Find max column index
        let maxCol = 0;
        for (const key of cellKeys) {
            const colIndex = parseInt(key);
            if (colIndex > maxCol) {
                maxCol = colIndex;
            }
        }

        // Build row values
        const rowValues: string[] = [];
        for (let col = 0; col <= maxCol; col++) {
            const cell = cells[col];
            rowValues.push(cell?.text || '');
        }

        csvLines.push(rowValues.join(';'));
    }

    const csvContent = csvLines.join('\n');
    downloadCSV(csvContent, 'export');
}

// Public method to reload data from parent
function reloadData(newData: SpreadsheetData) {
    if (newData && spreadsheetInstance) {
        loadData(newData);
    }
}

expose({ reloadData, getData });

/**
 * Initializes address validity in rowState based on pre-matched addresses from validation
 * Rows with pre-matched address options are considered valid
 */
function initializeAddressValidityFromProps() {
    const addressColumn = props.columns.find((col) => col.key === '_addressDropdown');
    if (!addressColumn?.rowDropdownOptions) return;

    for (const rowKey of Object.keys(addressColumn.rowDropdownOptions)) {
        const rowIndex = parseInt(rowKey);
        if (addressColumn.rowDropdownOptions[rowIndex]?.length > 0) {
            if (!rowState[rowIndex]) rowState[rowIndex] = {};
            rowState[rowIndex].addressValid = true;
        }
    }
}

/**
 * Initializes rowState client IDs from rowMatchedClientIds prop
 * This creates a single source of truth for client IDs
 */
function initializeClientIdsFromProps() {
    const addressColumn = props.columns.find((col) => col.key === '_addressDropdown');
    if (!addressColumn?.rowMatchedClientIds) return;

    for (const rowKey of Object.keys(addressColumn.rowMatchedClientIds)) {
        const rowIndex = parseInt(rowKey);
        const clientId = addressColumn.rowMatchedClientIds[rowIndex];
        if (clientId) {
            if (!rowState[rowIndex]) rowState[rowIndex] = {};
            rowState[rowIndex].clientId = clientId;
        }
    }
}

async function initSpreadsheet() {
    if (!spreadsheetRef.value) {
        return;
    }

    await nextTick();

    spreadsheetInstance = createSpreadsheetInstance(baseConfig.value);

    if (!spreadsheetInstance) {
        return;
    }

    spreadsheetInstance.change((data) => {
        emit('data-change', data as SpreadsheetData);
    });

    // Load data if provided
    if (props.data) {
        loadData(props.data, false); // Don't preserve scroll on initial load
        // Initialize client IDs and validation state from props
        initializeClientIdsFromProps();
        initializeAddressValidityFromProps();
        nextTick(() => {
            updateCellValidationStyles();
        });
    }

    // Listen for cell changes
    spreadsheetInstance.on('cell-edited', (text: string, ri: number, ci: number) => {
        // Check if this is a client column (Stranka) being edited
        const editedColumn = props.columns[ci];
        if (editedColumn && editedColumn.key === 'Stranka') {
            // Client cell was edited - clear associated data for this row
            if (rowState[ri]) {
                delete rowState[ri].clientAddresses;
                delete rowState[ri].clientId;
            }

            // Mark only Stranka as valid (clear red permanently)
            markCellsValid(ri, [ci]);
        }

        // For Kraj or Naslov edits, also mark valid
        if (editedColumn && (editedColumn.key === 'Kraj' || editedColumn.key === 'Naslov')) {
            markCellsValid(ri, [ci]);
        }

        emit('data-change', getData());
    });
}

/**
 * Applies summary row value to the last row of the spreadsheet data
 * Dynamically determines the last row based on data and minimum row count
 */
function applySummaryRow(data: SpreadsheetData): SpreadsheetData {
    if (!props.summaryRow) {
        return data;
    }

    const { columnIndex, value } = props.summaryRow;

    // Dynamically calculate the last row index based on actual data and minimum config
    const dataRowCount = Object.keys(data.rows || {}).length;
    const effectiveRowCount = Math.max(dataRowCount, 12);
    const summaryRowIndex = effectiveRowCount - 1;

    // Ensure rows object exists
    if (!data.rows) {
        data.rows = {};
    }

    // Ensure the summary row exists
    if (!data.rows[summaryRowIndex]) {
        data.rows[summaryRowIndex] = { cells: {} };
    }

    // Ensure cells object exists for the summary row
    if (!data.rows[summaryRowIndex].cells) {
        data.rows[summaryRowIndex].cells = {};
    }

    // Set the summary value at the specified column index
    data.rows[summaryRowIndex].cells[columnIndex] = {
        text: String(value),
        style: 0, // Use header style (bold) for summary
    };

    return data;
}

function loadData(data: SpreadsheetData, preserveScroll = true) {
    if (!spreadsheetInstance) {
        return;
    }

    const scrollPos = preserveScroll ? saveScrollPosition() : null;

    try {
        // Apply summary row if configured
        const dataWithSummary = applySummaryRow({ ...data, rows: { ...data.rows } });
        spreadsheetInstance.loadData([dataWithSummary]);
        updateCounts(dataWithSummary);
    } catch (error) {
        console.error('Error loading spreadsheet data:', error);
    }

    if (scrollPos) {
        // Use double RAF to ensure DOM is fully updated
        if (!preserveScroll) {
            return;
        }

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                restoreScrollPosition(scrollPos);
            });
        });
    }
}

function createSpreadsheetInstance(config: SpreadsheetConfig): Spreadsheet | null {
    if (!spreadsheetRef.value) {
        return null;
    }

    // Clear previous instance
    spreadsheetRef.value.innerHTML = '';

    const instance = new Spreadsheet(spreadsheetRef.value, {
        mode: props.readOnly ? 'read' : 'edit',
        showToolbar: config.showToolbar ?? props.showToolbar,
        showGrid: true,
        showContextmenu: config.showContextmenu ?? !props.readOnly,
        view: {
            height: () => config.viewHeight,
            width: () => config.viewWidth,
        },
        row: {
            len: config.rowCount,
            height: 28,
        },
        col: {
            len: config.colCount,
            width: 100,
            indexWidth: 60,
            minWidth: 60,
        },
        style: {
            bgcolor: '#ffffff',
            align: 'left',
            valign: 'middle',
            textwrap: false,
            strike: false,
            underline: false,
            color: '#0a0a0a',
            font: {
                name: 'Helvetica',
                size: 10,
                bold: false,
                italic: false,
            },
        },
    });

    // Extend context menu with custom items
    extendContextMenu(instance);

    return instance;
}

onMounted(() => {
    initSpreadsheet();
});

// Watch for summaryRow value changes - update only the summary cell without reloading
watch(
    () => props.summaryRow?.value,
    (newValue) => {
        if (newValue !== undefined && spreadsheetInstance && props.data) {
            updateSummaryCellOnly();
        }
    },
);

/**
 * Updates only the summary cell value without reloading all data
 * This preserves user focus and avoids infinite loops
 */
function updateSummaryCellOnly() {
    if (!props.summaryRow || !spreadsheetInstance) {
        return;
    }

    const { columnIndex, value } = props.summaryRow;

    // Calculate summary row index dynamically
    const currentData = getData();
    const dataRowCount = Object.keys(currentData?.rows || props.data?.rows || {}).length;
    const effectiveRowCount = Math.max(dataRowCount, 12);
    const summaryRowIndex = effectiveRowCount - 1;

    // Use x-data-spreadsheet's cellText method to update only the specific cell
    // This doesn't trigger a full reload and preserves focus
    spreadsheetInstance.cellText(summaryRowIndex, columnIndex, String(value), 0);
}

onBeforeUnmount(() => {
    if (spreadsheetInstance && spreadsheetRef.value) {
        spreadsheetRef.value.innerHTML = '';
        spreadsheetInstance = null;
    }
});

return { __sfc: true,props, emit, containerRef, spreadsheetRef, spreadsheetInstance, selectedRowIndex, selectedColIndex, rowCount, columnCount, dropdown, dropdownSearchInputRef, rowState, clientNameToIdMap, saveScrollPosition, restoreScrollPosition, filteredDropdownOptions, showTypeMoreHint, dropdownStyle, baseConfig, updateCounts, extendContextMenu, showDropdownForCell, hideDropdown, fetchAddressesByClient, fetchAddressesGeneral, fetchClients, debouncedSearch, handleDropdownSearchInput, handleDropdownOptionClick, handleClientSelection, addCustomContextMenuItems, createMenuItem, moveRow, moveColumn, getData, markCellsValid, updateCellValidationStyles, hasInitialClientMatch, isValidClientName, handleExport, reloadData, initializeAddressValidityFromProps, initializeClientIdsFromProps, initSpreadsheet, applySummaryRow, loadData, createSpreadsheetInstance, updateSummaryCellOnly }
}

})